import type { SessionUser } from 'types/game';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5002/api/v1';
const TOKEN_KEY = 'tp_player_token';
const UNLOAD_GUARD_KEY = 'tp:session-unload-guard';
const SPA_NAV_TS_KEY = 'tp:spa-navigation-ts';
const TABLE_LEAVE_KEY = 'tp:table-leave-ts';
const TABLE_BG_NAV_KEY = 'tp:table-bg-nav-ts';
const LEAVE_WALLET_KEY = 'tp:leave-wallet';

/** Covers mobile pagehide lag and post-leave wallet/session sync races. */
const IN_APP_NAV_RACE_MS = 15000;

const SESSION_USER_CACHE_KEY = 'tp:session-user-cache';

let tableLeaveGraceUntil = 0;
let launchExchangeInProgress = false;

export function clearSessionUserCache(): void {
  sessionStorage.removeItem(SESSION_USER_CACHE_KEY);
}

export function readSessionUserCache(): SessionUser | null {
  try {
    const raw = sessionStorage.getItem(SESSION_USER_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SessionUser;
  } catch {
    return null;
  }
}

export function writeSessionUserCache(user: SessionUser): void {
  sessionStorage.setItem(SESSION_USER_CACHE_KEY, JSON.stringify(user));
}

/** Consumer web-url entry — `/?session=<launch-token>` (hash URLs supported). */
export function getLaunchTokenFromUrl(): string | null {
  if (typeof window === 'undefined') return null;

  const fromSearch = new URLSearchParams(window.location.search).get('session');
  if (fromSearch) return fromSearch;

  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;
  const query = hash.includes('?') ? hash.slice(hash.indexOf('?') + 1) : hash;
  return new URLSearchParams(query).get('session');
}

export function hasLaunchTokenInUrl(): boolean {
  return getLaunchTokenFromUrl() != null;
}

export function beginLaunchExchange(): void {
  launchExchangeInProgress = true;
  clearToken();
  clearSessionUserCache();
  sessionStorage.removeItem(SPA_NAV_TS_KEY);
  sessionStorage.removeItem(TABLE_LEAVE_KEY);
  tableLeaveGraceUntil = 0;
}

export function endLaunchExchange(): void {
  launchExchangeInProgress = false;
}

export function isLaunchExchangeInProgress(): boolean {
  return launchExchangeInProgress || inflightLaunchExchange != null;
}

/** Call after post-launch navigation so stale requests cannot flash session-expired. */
export function finishLaunchExchange(): void {
  endLaunchExchange();
}

/** Build a minimal session user from a game JWT when /session fetch is temporarily unavailable. */
export function parseSessionUserFromToken(token: string): SessionUser | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;
    const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(normalized)) as {
      userId?: number;
      username?: string;
      currency?: string;
      variant?: string;
    };
    if (!payload.userId) return null;
    const currency = String(payload.currency || '');
    return {
      userId: Number(payload.userId),
      username: String(payload.username || 'Player'),
      balance: 0,
      totalBalance: 0,
      currency,
      currencySymbol: currency,
      variant: String(payload.variant || 'classic'),
    };
  } catch {
    return null;
  }
}

let inflightLaunchExchange: Promise<boolean> | null = null;
let inflightLaunchExchangeKey: string | null = null;

/** Single-flight consumer launch — survives React StrictMode remounts. */
export function runLaunchExchangeOnce(
  launchToken: string,
  exchange: () => Promise<boolean>
): Promise<boolean> {
  if (inflightLaunchExchange && inflightLaunchExchangeKey === launchToken) {
    return inflightLaunchExchange;
  }
  inflightLaunchExchangeKey = launchToken;
  beginLaunchExchange();
  inflightLaunchExchange = exchange().then((ok) => {
    inflightLaunchExchange = null;
    inflightLaunchExchangeKey = null;
    return ok;
  });
  return inflightLaunchExchange;
}

function isInAppNavigationMarked(): boolean {
  const ts = Number(sessionStorage.getItem(SPA_NAV_TS_KEY) || 0);
  return ts > 0 && Date.now() - ts < IN_APP_NAV_RACE_MS;
}

function isRecentTableLeave(): boolean {
  const ts = Number(sessionStorage.getItem(TABLE_LEAVE_KEY) || 0);
  return ts > 0 && Date.now() - ts < IN_APP_NAV_RACE_MS;
}

/** True while leaving a table — suppress spurious session-expired redirects. */
export function isInTableLeaveGrace(): boolean {
  return Date.now() < tableLeaveGraceUntil || isRecentTableLeave();
}

export function shouldSuppressSessionExpired(): boolean {
  return launchExchangeInProgress || isInTableLeaveGrace() || isInAppNavigationMarked();
}

/** Call before React Router navigation (mobile pagehide can fire on route change). */
export function markInAppNavigation(): void {
  sessionStorage.setItem(SPA_NAV_TS_KEY, String(Date.now()));
}

/** Leave / back from table — keep game session alive. */
export function markIntentionalTableLeave(): void {
  markInAppNavigation();
  sessionStorage.setItem(TABLE_LEAVE_KEY, String(Date.now()));
  sessionStorage.removeItem(UNLOAD_GUARD_KEY);
  tableLeaveGraceUntil = Date.now() + IN_APP_NAV_RACE_MS;
}

/** Stash cashed-out wallet until session refresh confirms leave (survives lobby navigation). */
export function stashLeaveWalletBalance(wallet: number): void {
  sessionStorage.setItem(
    LEAVE_WALLET_KEY,
    JSON.stringify({ wallet: Math.max(0, Math.floor(Number(wallet) || 0)), ts: Date.now() })
  );
}

export function readRecentLeaveWalletBalance(): number | null {
  try {
    const raw = sessionStorage.getItem(LEAVE_WALLET_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { wallet?: number; ts?: number };
    const ts = Number(parsed.ts) || 0;
    if (ts <= 0 || Date.now() - ts >= IN_APP_NAV_RACE_MS) {
      sessionStorage.removeItem(LEAVE_WALLET_KEY);
      return null;
    }
    return Math.max(0, Math.floor(Number(parsed.wallet) || 0));
  } catch {
    sessionStorage.removeItem(LEAVE_WALLET_KEY);
    return null;
  }
}

export function clearLeaveWalletBalance(): void {
  sessionStorage.removeItem(LEAVE_WALLET_KEY);
}

/** Temporarily open wallet (or another page) from the table without leaving the seat. */
export function markTableBackgroundNavigation(): void {
  markInAppNavigation();
  sessionStorage.setItem(TABLE_BG_NAV_KEY, String(Date.now()));
}

export function consumeTableBackgroundNavigation(): boolean {
  const ts = sessionStorage.getItem(TABLE_BG_NAV_KEY);
  sessionStorage.removeItem(TABLE_BG_NAV_KEY);
  if (!ts) return false;
  return Date.now() - Number(ts) < IN_APP_NAV_RACE_MS;
}

/** App boot: drop stale unload guard from older bundles (never revoke session here). */
export function reconcileSessionAfterUnload(): void {
  sessionStorage.removeItem(UNLOAD_GUARD_KEY);
}

export interface ApiResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  pagination?: { nextCursor: number | null; hasMore: boolean };
  error_code?: string;
}

export const SESSION_EXPIRED_EVENT = 'tp:session-expired';

let sessionExpiredEmitted = false;

export function resetSessionExpiredFlag(): void {
  sessionExpiredEmitted = false;
}

export function emitSessionExpired(options?: { force?: boolean }): void {
  if (!options?.force && shouldSuppressSessionExpired()) return;
  if (sessionExpiredEmitted) return;
  sessionExpiredEmitted = true;
  clearToken();
  clearSessionUserCache();
  window.dispatchEvent(
    new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { force: Boolean(options?.force) } })
  );
}

export function getToken(): string | null {
  const local = localStorage.getItem(TOKEN_KEY);
  if (local) return local;

  // One-time migration from sessionStorage (older builds stored token per tab).
  const session = sessionStorage.getItem(TOKEN_KEY);
  if (session) {
    localStorage.setItem(TOKEN_KEY, session);
    sessionStorage.removeItem(TOKEN_KEY);
    return session;
  }
  return null;
}

/** Persist game session token — survives refresh, navigation, and tab close in the same browser. */
export function setToken(token: string): void {
  resetSessionExpiredFlag();
  localStorage.setItem(TOKEN_KEY, token);
  sessionStorage.removeItem(TOKEN_KEY);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
}

export function revokeSessionOnServer(token: string): void {
  if (!token) return;
  fetch(`${API_BASE}/frontend/session/end`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    keepalive: true,
  }).catch(() => {});
}

/** Collapse concurrent identical GETs (e.g. React StrictMode double-mount in dev). */
const inflightGetRequests = new Map<string, Promise<ApiResponse<unknown>>>();

/** Exchange short-lived launch token from web-url for a game session token. */
const launchExchangeInflight = new Map<string, Promise<ApiResponse<{ token: string }>>>();

export async function exchangeLaunchToken(
  launchToken: string
): Promise<ApiResponse<{ token: string; session?: SessionUser }>> {
  const existing = launchExchangeInflight.get(launchToken);
  if (existing) return existing;

  const requestPromise = fetch(`${API_BASE}/frontend/session/exchange`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: launchToken }),
  })
    .then(async (res) => {
      try {
        return (await res.json()) as ApiResponse<{ token: string; session?: SessionUser }>;
      } catch {
        return {
          success: false,
          message: 'Invalid server response',
          error_code: 'network',
        } satisfies ApiResponse<{ token: string; session?: SessionUser }>;
      }
    })
    .catch((err: unknown) => ({
      success: false,
      message: err instanceof Error ? err.message : 'Network error',
      error_code: 'network',
    } satisfies ApiResponse<{ token: string; session?: SessionUser }>))
    .finally(() => {
      launchExchangeInflight.delete(launchToken);
    });

  launchExchangeInflight.set(launchToken, requestPromise);
  return requestPromise;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  } catch (err) {
    return {
      success: false,
      message: err instanceof Error ? err.message : 'Network error',
      error_code: 'network',
    };
  }

  let data: ApiResponse<T>;
  try {
    data = (await res.json()) as ApiResponse<T>;
  } catch {
    return {
      success: false,
      message: 'Invalid server response',
      error_code: 'network',
    };
  }

  if (token && getToken() === token && (res.status === 401 || data.error_code === '100')) {
    emitSessionExpired();
  }

  return data;
}

export const api = {
  get: <T>(path: string) => {
    const token = getToken();
    const cacheKey = `${path}::${token || ''}`;
    const existing = inflightGetRequests.get(cacheKey);
    if (existing) return existing as Promise<ApiResponse<T>>;

    const promise = request<T>(path).finally(() => {
      inflightGetRequests.delete(cacheKey);
    });
    inflightGetRequests.set(cacheKey, promise as Promise<ApiResponse<unknown>>);
    return promise;
  },
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
};

export default api;
